/**
 * "Report a bug" from the HaulierMagic CoPilot (CU-14zefhy1qnh, item 7; twin of the RouteMagic one).
 * The assistant drafts the report in a normal chat turn that ends with a fenced `hm-bug` JSON block; the chat shows it
 * as an editable form and nothing is filed until the user presses Submit (the server then creates a ClickUp task).
 * Pure helpers live here so they can be unit tested without the component.
 */

export interface BugDraft {
    title: string;
    steps: string;
    expected: string;
    actual: string;
}

/**
 * editing = form open · submitting = request in flight · submitted = filed (taskUrl set) · dismissed = user closed it.
 * An error keeps the card in 'editing' with `error` set, so the user can fix and resubmit.
 */
export type BugCardState = 'editing' | 'submitting' | 'submitted' | 'dismissed';

export interface BugCard {
    draft: BugDraft;
    state: BugCardState;
    /** the frozen screen contexts that went with the drafting turn (sent with the report; never edited here) */
    contexts?: any[];
    /** `screen-*` names of screenshots in this chat that go with the report (max 3) */
    screenshots?: string[];
    /** link to the created task once submitted */
    taskUrl?: string;
    /** filed, but something did not go with it (e.g. a screenshot could not be attached) */
    warning?: string;
    /** last submit error, shown under the form */
    error?: string;
}

export interface BugReportBody {
    title: string;
    steps: string;
    expected: string;
    actual: string;
    contexts?: any[];
    screenshots?: string[];
}

export const BUG_TITLE_MAX = 200;
export const BUG_FIELD_MAX = 4000;
export const BUG_MAX_SCREENSHOTS = 3;

/** The fenced bug block the model writes (`hm-bug`; `rm-bug` accepted too). */
const BUG_BLOCK = /```(?:hm|rm)-bug[^\n]*\n([\s\S]*?)```/;
const BUG_BLOCK_ALL = /```(?:hm|rm)-bug[^\n]*\n[\s\S]*?```/g;

/**
 * What the user's "Report a bug" click sends as a normal chat turn. The format instruction travels in the message
 * itself, so the drafting works whatever the server prompt says.
 */
export const BUG_DRAFT_PROMPT =
    'Help me report a bug about what went wrong in this chat. Using our conversation and the attached screen details, ' +
    'draft a short bug report. Do not file anything and do not change any data. End your reply with a fenced block:\n' +
    '```hm-bug\n{"title": "...", "steps": "1. ...\\n2. ...", "expected": "...", "actual": "..."}\n```\n' +
    'Keep the title under 100 characters. Leave a field empty when you do not know it.';

/** What the user bubble shows for that turn (the instruction itself is long and not for reading). */
export const BUG_DRAFT_LABEL = '🐞 Help me report a bug';

function text(value: any, max: number): string {
    if (value === null || value === undefined) {
        return '';
    }
    if (Array.isArray(value)) {
        value = value
            .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
            .map((v, i) => (/^\s*\d+[.)]/.test(String(v)) ? String(v).trim() : `${i + 1}. ${String(v).trim()}`))
            .join('\n');
    }
    if (typeof value === 'object') {
        return '';
    }
    const s = String(value).trim();
    return s.length > max ? s.substring(0, max) : s;
}

/** Normalises a draft object from the model / server; null when it has nothing usable. */
export function toBugDraft(raw: any): BugDraft | null {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return null;
    }
    const draft: BugDraft = {
        title: text(raw.title ?? raw.summary, BUG_TITLE_MAX),
        steps: text(raw.steps ?? raw.stepsToReproduce ?? raw.reproduce, BUG_FIELD_MAX),
        expected: text(raw.expected, BUG_FIELD_MAX),
        actual: text(raw.actual, BUG_FIELD_MAX)
    };
    return draft.title || draft.steps || draft.expected || draft.actual ? draft : null;
}

/** An empty draft: the form still opens when the model gave no block, so the user can write it themselves. */
export function emptyBugDraft(): BugDraft {
    return {title: '', steps: '', expected: '', actual: ''};
}

/** Reads the fenced `hm-bug` JSON from a reply; null when there is none or it is not valid JSON. */
export function parseBugBlock(reply: string): BugDraft | null {
    const match = BUG_BLOCK.exec(reply || '');
    if (!match) {
        return null;
    }
    try {
        return toBugDraft(JSON.parse(match[1].trim()));
    } catch (e) {
        return null;
    }
}

/** Removes any fenced bug block from the reply text shown in the bubble. */
export function stripBugBlock(reply: string): string {
    if (!reply || !/```(?:hm|rm)-bug/.test(reply)) {
        return reply;
    }
    return reply
        .replace(BUG_BLOCK_ALL, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

/** Submit needs a title and at least one of steps / actual (what went wrong). */
export function canSubmitBug(card: BugCard | null | undefined): boolean {
    if (!card || card.state !== 'editing') {
        return false;
    }
    const d = card.draft || emptyBugDraft();
    return !!d.title.trim() && !!(d.steps.trim() || d.actual.trim());
}

/** The request body; optional lists left out when empty. */
export function bugReportBody(card: BugCard): BugReportBody {
    const d = card.draft;
    const body: BugReportBody = {
        title: d.title.trim().substring(0, BUG_TITLE_MAX),
        steps: d.steps.trim().substring(0, BUG_FIELD_MAX),
        expected: d.expected.trim().substring(0, BUG_FIELD_MAX),
        actual: d.actual.trim().substring(0, BUG_FIELD_MAX)
    };
    if (card.contexts?.length) {
        body.contexts = card.contexts;
    }
    const shots = (card.screenshots || []).filter((s) => !!s);
    if (shots.length) {
        body.screenshots = shots.slice(-BUG_MAX_SCREENSHOTS);
    }
    return body;
}

/** Screenshot names to send with a report: the drafting turn's newest accepted screenshots (max 3, de-duplicated). */
export function bugScreenshots(names: string[], isScreen: (name: string) => boolean): string[] {
    const list = (names || []).filter((n) => !!n && isScreen(n));
    return list.filter((n, i) => list.indexOf(n) === i).slice(-BUG_MAX_SCREENSHOTS);
}

/** User-facing text for a failed submit (429 = rate limit; 400 = the server's own reason). */
export function bugSubmitError(error: any): string {
    const status = error?.status;
    const body = error?.error;
    let reason = '';
    if (typeof body === 'string' && !body.trim().startsWith('{') && !body.trim().startsWith('<')) {
        reason = body.trim();
    } else if (body && typeof body === 'object') {
        reason = [body.error, body.detail, body.title].find((v) => typeof v === 'string' && v.trim() && !/^error\.[\w.]+$/.test(v.trim())) || '';
    }
    reason = reason.trim();
    if (status === 429) {
        return reason || 'You can report up to 5 bugs an hour. Try again later.';
    }
    if (status === 404 || status === 501 || status === 503) {
        return 'Bug reporting is not available right now.';
    }
    // 400 bad input, 502 the tracker failed: the server's own words
    return reason && status && (status < 500 || status === 502) ? reason : "Couldn't send the report. Try again.";
}

/**
 * A reply worth offering "Report a bug" under: the answer to an "Ask CoPilot" error question, or a reply that talks
 * about an error / failure. Error placeholders (the chat itself failed) qualify too.
 */
export function explainsError(reply: string, question?: string, failed?: boolean): boolean {
    if (failed) {
        return true;
    }
    if (/^why did i get this error/i.test((question || '').trim())) {
        return true;
    }
    return /\b(error|errors|exception|failed|failing|fails|bug|broken|not working|doesn't work|does not work|cannot|can't|couldn't|unable to)\b/i.test(
        reply || ''
    );
}

/** After a reload: a submit in flight goes back to editing (the user may resend; the server rate-limits). */
export function restoreBugCard(card: BugCard): BugCard {
    return card.state === 'submitting' ? {...card, state: 'editing'} : card;
}
